import { useState } from "react";
import { useLocation } from "wouter";
import { Sun, BookOpen, Heart, Compass, User, Wrench, Users, MessageSquare } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin } from "@/lib/admin";
import { usePlausible } from "@/hooks/usePlausible";
import { FeedbackModal } from "./FeedbackModal";

type TabDef = {
  label: string;
  path: string;
  Icon: React.ComponentType<{ className?: string }>;
};

const ALL_TABS: TabDef[] = [
  { label: "Daily",     path: "/",          Icon: Sun      },
  { label: "Library",   path: "/library",   Icon: BookOpen },
  { label: "Community", path: "/community", Icon: Users    },
  { label: "Favorites", path: "/favorites", Icon: Heart    },
  { label: "Discover",  path: "/discover",  Icon: Compass  },
  { label: "Profile",   path: "/profile",   Icon: User     },
  { label: "Harvest",   path: "/harvest",   Icon: Wrench   },
];

export function MobileNav({ activeNav }: { activeNav: string }) {
  const [, navigate] = useLocation();
  const { currentUser } = useAuth();
  const { trackEvent } = usePlausible();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const tabs = ALL_TABS.filter(
    ({ label }) => label !== "Harvest" || isAdmin(currentUser?.uid),
  );

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 flex border-t border-stone-200 bg-white pb-safe lg:hidden"
        data-testid="mobile-nav"
      >
        {tabs.map(({ label, path, Icon }) => {
          const active = activeNav === label;
          return (
            <button
              key={label}
              onClick={() => { if (path === "/discover") trackEvent("Discovery Used"); navigate(path); }}
              data-testid={`mobile-nav-${label}`}
              className={
                "flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-0.5 py-2.5 transition-colors active:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stone-400 " +
                (active ? "text-stone-900" : "text-stone-400")
              }
              aria-current={active ? "page" : undefined}
            >
              <span
                className={
                  "flex items-center justify-center rounded-full px-2.5 py-1 transition-colors " +
                  (active ? "bg-stone-100" : "")
                }
              >
                <Icon className="h-5 w-5" />
              </span>
              <span
                className={
                  "w-full truncate text-center text-[10px] leading-none " +
                  (active ? "font-semibold" : "font-medium")
                }
              >
                {label}
              </span>
            </button>
          );
        })}
        <button
          onClick={() => setFeedbackOpen(true)}
          data-testid="mobile-nav-Feedback"
          className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-0.5 py-2.5 text-stone-400 transition-colors active:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stone-400"
        >
          <span className="flex items-center justify-center rounded-full px-2.5 py-1">
            <MessageSquare className="h-5 w-5" />
          </span>
          <span className="w-full truncate text-center text-[10px] font-medium leading-none">
            Feedback
          </span>
        </button>
      </nav>
      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
}
